'use client';

import { FormEvent, useEffect, useState } from 'react';
import { LoginLink } from '@/platform/auth/LoginLink';
import { useAuth } from '@/platform/auth/useAuth';
import { listOrderMessages, postOrderMessage, type OrderMessage } from './api';

type Props = {
  orderId: string;
  canCompose: boolean;
  guestHint?: string;
};

function formatWhen(iso: string) {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function roleLabel(role: OrderMessage['authorRole']) {
  switch (role) {
    case 'buyer':
      return 'Buyer';
    case 'seller':
      return 'Seller';
    default:
      return 'System';
  }
}

export function OrderMessageThread({ orderId, canCompose, guestHint }: Props) {
  const { isAuthenticated } = useAuth();
  const [messages, setMessages] = useState<OrderMessage[]>([]);
  const [body, setBody] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isAuthenticated || !canCompose) {
      setLoading(false);
      return;
    }
    listOrderMessages(orderId)
      .then((r) => setMessages(r.messages))
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, [orderId, isAuthenticated, canCompose]);

  async function sendMessage(e: FormEvent) {
    e.preventDefault();
    const text = body.trim();
    if (!text) return;
    setSending(true);
    setError('');
    try {
      const created = await postOrderMessage(orderId, text);
      setMessages((prev) => [...prev, created]);
      setBody('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not send message');
    } finally {
      setSending(false);
    }
  }

  if (!isAuthenticated) {
    return (
      <section className="order-message-thread">
        <h2 className="buyer-order-detail__section">Order messages</h2>
        <p className="text-muted">
          {guestHint ?? 'Sign in to read and send messages about this order.'}
        </p>
        <p>
          <LoginLink className="btn btn-primary" returnTo={`/marketplace/orders/${orderId}/`}>
            Sign in
          </LoginLink>
        </p>
      </section>
    );
  }

  if (!canCompose) {
    return (
      <section className="order-message-thread">
        <h2 className="buyer-order-detail__section">Order messages</h2>
        <p className="text-muted">Messages are available after this order is linked to your account.</p>
      </section>
    );
  }

  return (
    <section className="order-message-thread">
      <h2 className="buyer-order-detail__section">Order messages</h2>
      <p className="text-muted vendor-console__hint">
        Reply to thread alert emails to post here. Receipt emails do not open the thread.
      </p>
      {loading && <p className="text-muted">Loading messages…</p>}
      {error && <p className="form-error">{error}</p>}
      {messages.length === 0 && !loading ? (
        <p className="text-muted">No messages yet. Send a note to the other party below.</p>
      ) : (
        <ul className="order-message-thread__list">
          {messages.map((msg) => (
            <li key={msg.id} className={`order-message-thread__item order-message-thread__item--${msg.authorRole}`}>
              <p className="order-message-thread__meta">
                <strong>{roleLabel(msg.authorRole)}</strong>
                <span className="text-muted"> · {formatWhen(msg.createdAt)}</span>
                {msg.source === 'email_inbound' && <span className="text-muted"> · via email</span>}
              </p>
              <p className="order-message-thread__body">{msg.body}</p>
            </li>
          ))}
        </ul>
      )}
      <form onSubmit={(e) => void sendMessage(e)} className="order-message-thread__compose">
        <label>
          Message
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={3}
            required
            disabled={sending}
            placeholder="Ask about shipping, delivery, or order details…"
          />
        </label>
        <button type="submit" className="btn btn-primary" disabled={sending || !body.trim()}>
          {sending ? 'Sending…' : 'Send message'}
        </button>
      </form>
    </section>
  );
}
